import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Snackbar,
  Alert,
  IconButton,
  Stack,
  Zoom,
  Container,
  SnackbarContent,
} from "@mui/material";
import { RootState } from "../../../store/store";
import { hideNotification, setNotifications } from "../../../store/slices/notificationSlice";
import { useTheme } from "@mui/material/styles";
import IconComponent from "../IconComponent";
import { APP_ID } from "../../../_app/consts";
import Slide from "@mui/material/Slide";
import { SnackbarProvider } from "notistack";
import DOMPurify from "dompurify";
import { decodeUnicodeString } from "../../../_utils/decodeUnicodeString";

type Mode = "light" | "dark" | "system";

function getSystemMode(): "light" | "dark" {
  const prefersDarkMode =
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDarkMode ? "dark" : "light";
}

const NotificationComponent = () => {
  const dispatch = useDispatch();
  const notifications = useSelector((state: RootState) => state.notifications.notifications);
  const theme = useTheme();

  const [activeMode, setActiveMode] = useState<Mode>(() => {
    const savedMode = localStorage.getItem("activeMode" + APP_ID);
    return savedMode ? (savedMode as Mode) : "system";
  });

  const actualMode = useMemo(() => {
    return activeMode === "system" ? getSystemMode() : activeMode;
  }, [activeMode]);

  const [closedNotifications, setClosedNotifications] = useState<string[]>([]);

  const handleClose = (id: string, reason?: string) => {
    if (reason !== "clickaway") {
      // Добавляем уведомление в список "закрытых", чтобы запустить анимацию закрытия
      setClosedNotifications((prev) => [...prev, id]);
      // Через некоторое время удалим его из состояния Redux
      setTimeout(() => {
        dispatch(hideNotification(id));
        // Удаляем из списка закрытых уведомлений
        setClosedNotifications((prev) => prev.filter((closedId) => closedId !== id));
      }, 500); // Время совпадает с длительностью анимации (500 мс по умолчанию в MUI)
    }
  };

  useEffect(() => {
    if (notifications.length > 4 && notifications.length < 6) {
      let nots = [...notifications];
      nots = nots.splice(1);
      dispatch(setNotifications(nots));
    }
  }, [notifications]);

  const AdronSeverity = {
    dark: {
      icons: {
        success: <IconComponent iconName={"SuccessIcon"} type={"asBackground"} size={36} />,
        error: <IconComponent iconName={"ErrorTriangle"} type={"asBackground"} size={36} />,
        warning: <IconComponent iconName={"SuccessIcon"} type={"asBackground"} size={36} />,
        info: <></>,
        infoUpdated: "",
        successProfile: <IconComponent iconName={"Avatar"} type={"asBackground"} size={36} />,
        infoCopied: "",
      },
      bgColors: {
        success: "#0D1012",
        error: "#EF4444",
        warning: "#0D1012",
        info: "#0D1012",
        infoUpdated: "",
        successProfile: "#0D1012",
        infoCopied: "",
      },
      borderColors: {
        success: "#04D538",
        error: "#EF4444",
        warning: "#A2FF00",
        info: "#3A1890",
        infoUpdated: "",
        successProfile: "#04D538",
        infoCopied: "",
      },
      textColor: "#FAFAFA",
    },
    light: {
      icons: {
        success: <IconComponent iconName={"SuccessIcon"} type={"asBackground"} size={36} />,
        error: <IconComponent iconName={"ErrorTriangle"} type={"asBackground"} size={36} />,
        warning: <IconComponent iconName={"SuccessIcon"} type={"asBackground"} size={36} />,
        info: <></>,
        infoUpdated: "",
        successProfile: <IconComponent iconName={"Avatar"} type={"asBackground"} size={36} />,
        infoProfile: <IconComponent iconName={"Avatar"} type={"asBackground"} size={36} />,
        infoCopied: "",
      },
      bgColors: {
        success: "#f5f5f5",
        error: "#EF4444",
        warning: "#f5f5f5",
        info: "#f5f5f5",
        infoProfile: "#f5f5f5",
        infoUpdated: "#f5f5f5",
        successProfile: "#f5f5f5",
        infoCopied: "#f5f5f5",
      },
      borderColors: {
        success: "#04D538",
        error: "#EF4444",
        warning: "#A2FF00",
        info: "#D8D1E9",
        infoProfile: "#04D538",
        infoUpdated: "#04D538",
        successProfile: "#04D538",
        infoCopied: "#04D538",
      },
      textColor: "#09090B",
    },
  };

  return (
    <Container sx={{ width: "100%", position: "fixed", zIndex: 1400, bottom: "11%" }}>
      <Stack
        sx={{ width: "100%", zIndex: 1400, bottom: "11%" }}
        justifyContent={"center"}
        direction="column"
        spacing={1}
      >
        <SnackbarProvider maxSnack={3}>
          {notifications.map((notification) => (
            <Snackbar
              className={"AdronSnackBar"}
              sx={{ position: "relative", width: "100%", justifyContent: "center" }}
              key={notification.id}
              open={!closedNotifications.includes(notification.id)} // Закрываем с анимацией
              autoHideDuration={notification.autoHideDuration}
              onClose={(event, reason) => handleClose(notification.id, reason)}
              TransitionComponent={Slide}
            >
              <SnackbarContent
                onClick={() => handleClose(notification.id)}
                sx={{
                  backgroundColor:
                    notification.severity &&
                    AdronSeverity[actualMode].bgColors[notification.severity],
                  border: `1px solid ${notification.severity && AdronSeverity[actualMode].borderColors[notification.severity]}`,
                  color: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  width: "100% !important",
                }}
                message={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        color:
                          notification.severity === "error"
                            ? "#fff"
                            : AdronSeverity[actualMode].textColor,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          decodeUnicodeString(notification.message).replace(/\\n/g, "<br />")
                        ),
                      }}
                    ></span>
                    {notification.severity &&
                      AdronSeverity[actualMode].icons[notification.severity]}
                  </div>
                }
              />
            </Snackbar>
          ))}
        </SnackbarProvider>
      </Stack>
    </Container>
  );
};

export default NotificationComponent;
